// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xHeaderUpdate } from "@conklin/App/Handlers/XNG/Header";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import { checkoutPages } from "@conklin/XDL/constants";
import IconClose from "@conklin/XDL/Icons/IconClose";
import IconSearch from "@conklin/XDL/Icons/IconSearch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentPage, getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { LinkModel } from "@insite/client-framework/Store/Links/LinksSelectors";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import { HiddenProps } from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Changes
const mapStateToProps = (state: CustomAppState) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Basic/LinkList";
        })?.fields.links as LinkModel[]) ?? [];
    let customerPhone = "";
    headerLinkListLinkFields.forEach(link => {
        if (link.fields.destination.value.indexOf("tel") > -1) {
            customerPhone = link.fields.overriddenTitle || link.fields.destination.value;
        }
    });
    return {
        mobileSearchOpen: state.xHeader.mobileSearchOpen,
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
        customerPhone,
    };
};

// XNG-Changes
const mapDispatchToProps = {
    setNavDrawerIsOpen,
    xHeaderUpdate,
};

// XNG-Changes
type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface LogoLinksSearchStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    linkListHidden: HiddenProps;
    // XNG-Changes
    actionsWrapper?: InjectableCss;
    mobileSearch?: ClickableProps;
    accountContainer?: InjectableCss;
    customerPhone?: LinkPresentationProps;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            padding: 20px 45px;
            margin: 0 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-grow: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            img {
                height: 78px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                height: 42px;
                                width: auto;
                                margin: 0;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;
            padding: 20px 45px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        css`
                            flex-grow: 8;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    linkListHidden: {
        below: "lg",
    },
};

const LogoLinksSearch: React.FC<Props> = ({
    id,
    // XNG-Changes
    xHeaderUpdate,
    mobileSearchOpen,
    isMobileScreen,
    isTabletScreen,
    isCheckoutPage,
    setNavDrawerIsOpen,
    customerPhone,
}) => {
    // XNG-Changes
    React.useEffect(() => {
        xHeaderUpdate({ key: "mobileSearchOpen", value: false });
    }, [isMobileScreen, isTabletScreen]);

    // XNG-Changes: Checkout Header
    if (isCheckoutPage) {
        return (
            <StyledWrapper {...logoLinksSearchStyles.container} {...{ isCheckout: true }}>
                <StyledWrapper {...logoLinksSearchStyles.logoWrapper}>
                    <Zone contentId={id} zoneName="Logo" />
                </StyledWrapper>

                {customerPhone && (
                    <Link href={`tel:${customerPhone}`} {...logoLinksSearchStyles.customerPhone}>
                        {customerPhone}
                    </Link>
                )}
            </StyledWrapper>
        );
    }

    // XNG-Changes
    const handleMobileSearchClick = () => {
        xHeaderUpdate({ key: "mobileSearchOpen", value: !mobileSearchOpen });
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
    };

    return (
        // XNG-Changes: structure
        <StyledWrapper {...logoLinksSearchStyles.container}>
            {(mobileSearchOpen || !isMobileScreen) && (
                <StyledWrapper {...logoLinksSearchStyles.searchMenuWrapper} {...{ mobileSearchOpen }}>
                    <Zone contentId={id} zoneName="Search" />
                </StyledWrapper>
            )}
            <StyledWrapper {...logoLinksSearchStyles.actionsWrapper}>
                {(isMobileScreen || mobileSearchOpen) && (
                    <Clickable
                        onClick={handleMobileSearchClick}
                        {...logoLinksSearchStyles.mobileSearch}
                        aria-label="Header Search"
                    >
                        <Icon
                            color="common.ui5"
                            src={mobileSearchOpen ? IconClose : IconSearch}
                            size={mobileSearchOpen ? 16 : 22}
                            className="menu-icon"
                        />
                    </Clickable>
                )}
                {!mobileSearchOpen && <Zone contentId={id} zoneName="actions" />}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const logoLinksSearch: WidgetModule = {
    // XNG-Changes
    component: connect(mapStateToProps, mapDispatchToProps)(LogoLinksSearch),
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
